import { getLanguageFromURL } from "helpers/common";
import { isEmpty } from "lodash";
import moment from "moment-timezone";

export const configurationData = {
  // Represents the resolutions for bars supported by your datafeed
  supported_resolutions: [
    // "1", // 1 minute
    // "5", // 5 minutes
    // "15", // 15 minutes
    // "30", // 30 minutes
    // "60", // 1 hour
    // "120", // 2 hours
    // "240", // 4 hours
    "1D", // 1 day
    "1W", // 1 week
    "1M" // 1 month
  ],

  // The `exchanges` arguments are used for the `searchSymbols` method if a user selects the exchange
  exchanges: [
    {
      value: "Tickerlines",
      name: "Tickerlines",
      desc: "Tickerlines",
    },
  ],
};

export const getWidgetOptions = (defaultProps, toolName, chartContainerRef, graph) => {
  return {
    symbol: toolName,
    overrides: {
      "linetoolpath.rightEnd": "normal",
      "linetoolpath.lineColor": "rgba( 21, 119, 96, 1)",
      "linetoolpath.lineWidth": 1,
    },
    // BEWARE: no trailing slash is expected in feed URL
    datafeed: {
      onReady: (callback) => {
        console.log("[onReady]: Method call");
        setTimeout(() =>
          callback({
            ...configurationData,
            symbols_types: { name: toolName, value: toolName },
            supported_resolutions: configurationData?.supported_resolutions
          })
        );
      },
      resolveSymbol: async (
        symbolName,
        onSymbolResolvedCallback,
        onResolveErrorCallback,
        extension
      ) => {
        console.log("[resolveSymbol]: Method call", symbolName);
  
        // Symbol information object
        const symbolInfo = {
          ticker: toolName,
          name: toolName,
          description: toolName,
          type: toolName,
          session: "24x7",
          timezone: "Etc/UTC",
          exchange: configurationData.exchanges[0].value,
          minmov: 1,
          pricescale: 100,
          has_intraday: false,
          has_no_volume: true,
          has_weekly_and_monthly: false,
          supported_resolutions: configurationData?.supported_resolutions,
          volume_precision: 2,
        };
  
        console.log("[resolveSymbol]: Symbol resolved", symbolName);
        onSymbolResolvedCallback(symbolInfo);
      },
      getBars: (symbolInfo, resolution, periodParams, onHistoryCallback, onErrorCallback) => {
        setTimeout(
            () => {
              // console.log("Bars debug: ", periodParams);
              const { from, to, firstDataRequest, countBack } = periodParams;

              let bars = [];
              let time = new Date(from * 1000);

              while (time.getTime() / 1000 <= to) {
                // Find bar in graph for the current timestamp
                const bar = graph.find(b => {
                  const barTime = new Date(b.timestamp).getTime() / 1000;
                  return barTime === time.getTime() / 1000;
                });

                if (bar) {
                  // Bar found in graph
                  bars.push({
                    time: new Date(bar.timestamp).getTime(),
                    low: bar.low,
                    high: bar.high,
                    open: bar.open,
                    close: bar.close,
                    volume: bar.volume,
                  });
                } else {
                  // Bar not found, create a new one with 0 price
                  bars.push({
                    time: time.getTime(),
                    low: 0,
                    high: 0,
                    open: 0,
                    close: 0,
                    volume: 0,
                  });
                }

                // Move to the next day or period (adjust as needed)
                time.setUTCDate(time.getUTCDate() + 1);
              }

              if (bars.length) {
                // console.log("Bars debug: Graph data processed");
                onHistoryCallback(bars, { noData: false });
              } else {
                console.log("Bars debug: No bars found");
                onHistoryCallback([], { noData: true });
              }
            },
            50
        );
      },
      subscribeBars: (
        symbolInfo,
        resolution,
        onRealtimeCallback,
        subscriberUID,
        onResetCacheNeededCallback
      ) => {
        console.log(
          "[subscribeBars]: Method call with subscriberUID:",
          subscriberUID
        );
      },
      unsubscribeBars: (subscriberUID) => {
        console.log(
          "[unsubscribeBars]: Method call with subscriberUID:",
          subscriberUID
        );
      },
    },
    interval: defaultProps.interval,
    container: chartContainerRef.current,
    library_path: defaultProps.libraryPath,
    debug: false,
    locale: getLanguageFromURL() || "en",
    disabled_features: [
      "use_localstorage_for_settings",
      "left_toolbar",
      "header_compare",
      "header_symbol_search",
      "header_interval_dialog_button",
      "header_settings",
      "header_fullscreen_button",
      "header_screenshot",
      "header_chart_type",
      "header_undo_redo",
      "header_saveload",
      "header_resolutions",
    ],
    enabled_features: [
      "header_widget",
      "header_indicators",
      "chart_zoom",
      "disable_drag_zoom",
      "show_toolbar"
    ],
    charts_storage_url: defaultProps.chartsStorageUrl,
    charts_storage_api_version: defaultProps.chartsStorageApiVersion,
    client_id: defaultProps.clientId,
    user_id: defaultProps.userId,
    // fullscreen: defaultProps.fullscreen,
    autosize: defaultProps.autosize,
    studies_overrides: defaultProps.studiesOverrides,
    loading_screen: {
      backgroundColor: "#ffffff",
      foregroundColor: "#0c3d1d",
    },
    width: "100%",
    style: 14,
  }
};

export const getTrendlineChartWidgetOptions = (defaultProps, toolName, chartContainerRef, graph) => {
  return {
    symbol: `${toolName} Trendline`,
    overrides: {
      "linetoolpath.rightEnd": "normal",
      "linetoolpath.lineColor": "rgba( 21, 119, 96, 1)",
      "linetoolpath.lineWidth": 1,
    },
    // BEWARE: no trailing slash is expected in feed URL
    datafeed: {
      onReady: (callback) => {
        console.log("[onReady]: Method call");
        setTimeout(() =>
          callback({
            ...configurationData,
            symbols_types: { name: toolName, value: toolName },
            supported_resolutions: ["1"]
          })
        );
      },
      resolveSymbol: async (
        symbolName,
        onSymbolResolvedCallback,
        onResolveErrorCallback,
        extension
      ) => {
        console.log("[resolveSymbol]: Method call", symbolName);
  
        // Symbol information object
        const symbolInfo = {
          ticker: toolName,
          name: toolName,
          description: toolName,
          type: toolName,
          session: "24x7",
          timezone: "Etc/UTC",
          exchange: configurationData.exchanges[0].value,
          minmov: 1,
          pricescale: 100,
          has_intraday: true,
          has_no_volume: true,
          has_weekly_and_monthly: false,
          supported_resolutions: ["1"],
          volume_precision: 2,
        };
  
        console.log("[resolveSymbol]: Symbol resolved", symbolName);
        onSymbolResolvedCallback(symbolInfo);
      },
      getBars: (symbolInfo, resolution, periodParams, onHistoryCallback, onErrorCallback) => {
        setTimeout(
          () => {
            const { from, to } = periodParams;

            const now = Math.floor(Date.now() / 1000);
            const oneYearInSeconds = 365 * 24 * 60 * 60;
            const extendedTo = Math.max(to, now + oneYearInSeconds);

            let bars = [];
            let time = new Date(from * 1000);
            time.setUTCSeconds(0);
            time.setUTCMilliseconds(0);

            while (time.getTime() / 1000 <= extendedTo) {
              bars.push({
                time: time.getTime(), // time in ms
                open: 0,
                high: 0,
                low: 0,
                close: 0,
                volume: 0,
              });

              // Advance by 1 minute
              time.setUTCMinutes(time.getUTCMinutes() + 1);
            }

            if (bars.length) {
              onHistoryCallback(bars, { noData: false });
            } else {
              onHistoryCallback([], { noData: true });
            }
          },
          50
        );
      },
      subscribeBars: (
        symbolInfo,
        resolution,
        onRealtimeCallback,
        subscriberUID,
        onResetCacheNeededCallback
      ) => {
        console.log(
          "[subscribeBars]: Method call with subscriberUID:",
          subscriberUID
        );
      },
      unsubscribeBars: (subscriberUID) => {
        console.log(
          "[unsubscribeBars]: Method call with subscriberUID:",
          subscriberUID
        );
      },
    },
    interval: defaultProps.interval,
    container: chartContainerRef.current,
    library_path: defaultProps.libraryPath,
    debug: false,
    locale: getLanguageFromURL() || "en",
    disabled_features: [
      "use_localstorage_for_settings",
      "left_toolbar",
      "header_compare",
      "header_symbol_search",
      "header_interval_dialog_button",
      "header_settings",
      "header_fullscreen_button",
      "header_screenshot",
      "header_chart_type",
      "header_undo_redo",
      "header_saveload",
      "header_resolutions",
    ],
    
    enabled_features: [
      // "study_templates",
      "chart_zoom",
      "disable_drag_zoom", // Disable drag zoom if you want to limit zoom to toolbar buttons
      "show_toolbar" // Ensures toolbar is visible
    ],
    charts_storage_url: defaultProps.chartsStorageUrl,
    charts_storage_api_version: defaultProps.chartsStorageApiVersion,
    client_id: defaultProps.clientId,
    user_id: defaultProps.userId,
    // fullscreen: defaultProps.fullscreen,
    autosize: defaultProps.autosize,
    studies_overrides: defaultProps.studiesOverrides,
    loading_screen: {
      backgroundColor: "#ffffff",
      foregroundColor: "#0c3d1d",
    },
    width: "100%",
    style: 14
  }
};